import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Inbox, Check, Bell, Lock, Unlock, KeyRound, User, Link2, Receipt, Archive, Building2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Små animerte scener (framer-motion) til gjennomgangene. Hver scene er selvforklarende uten tekst
 * og tar hensyn til prefers-reduced-motion (viser da sluttilstanden uten bevegelse).
 */

const Frame = ({ children, className }) => (
  <div className={cn("relative w-full h-full min-h-[190px] rounded-xl border bg-gradient-to-b from-secondary/70 to-card overflow-hidden", className)} aria-hidden="true">
    <div className="absolute inset-x-0 top-0 h-6 bg-card/70 border-b flex items-center gap-1.5 px-3">
      <span className="h-2 w-2 rounded-full bg-muted-foreground/30" /><span className="h-2 w-2 rounded-full bg-muted-foreground/30" /><span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
    </div>
    <div className="absolute inset-0 pt-6">{children}</div>
  </div>
);

const useAnim = () => {
  const reduced = useReducedMotion();
  // Med redusert bevegelse: alt vises ferdig umiddelbart
  const t = (delay = 0, duration = 0.5) => (reduced ? { duration: 0 } : { delay, duration, ease: "easeOut" });
  const loop = (extra) => (reduced ? {} : { repeat: Infinity, repeatDelay: 2.2, ...extra });
  return { reduced, t, loop };
};

const Doc = ({ className, children }) => (
  <div className={cn("rounded-md border bg-card shadow-sm p-2 space-y-1.5", className)}>
    <div className="h-1.5 w-2/3 rounded bg-muted-foreground/30" />
    <div className="h-1.5 w-1/2 rounded bg-muted-foreground/20" />
    <div className="h-1.5 w-3/5 rounded bg-muted-foreground/20" />
    {children}
  </div>
);

/* 1. Dokumenter flyr inn i en innboks */
export const FlowScene = () => {
  const { t, loop } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-between px-8">
        <div className="relative h-24 w-24">
          {[0, 1, 2].map((i) => (
            <motion.div key={i} className="absolute left-0 top-0 w-16" style={{ top: i * 10, left: i * 6 }}
              initial={{ x: 0, opacity: 1, rotate: -4 + i * 4 }} animate={{ x: [0, 150, 150], opacity: [1, 1, 0] }} transition={{ ...t(0.3 + i * 0.7, 1.2), ...loop({ repeatDelay: 1.2 }) }}>
              <Doc><FileText className="h-3.5 w-3.5 text-primary" /></Doc>
            </motion.div>
          ))}
        </div>
        <div className="relative">
          <div className="h-20 w-24 rounded-xl border-2 border-primary/40 bg-accent/70 flex items-center justify-center"><Inbox className="h-8 w-8 text-primary" /></div>
          <motion.span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center" initial={{ scale: 0 }} animate={{ scale: [0, 1.2, 1] }} transition={t(1.6, 0.4)}>3</motion.span>
        </div>
      </div>
    </Frame>
  );
};

/* 2. AI leser dokumentet – skannestrek + felt som dukker opp */
export const ScanScene = () => {
  const { t, reduced } = useAnim();
  const fields = ["Leverandør", "Beløp", "Forfall"];
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center gap-6 px-8">
        <div className="relative w-28">
          <Doc className="p-3"><div className="h-1.5 w-1/3 rounded bg-muted-foreground/20" /><div className="h-1.5 w-2/3 rounded bg-muted-foreground/20" /></Doc>
          {!reduced ? <motion.div className="absolute left-0 right-0 h-0.5 bg-primary shadow-[0_0_12px_2px_rgba(14,116,144,.6)]" initial={{ top: 4, opacity: 0 }} animate={{ top: [4, 88, 4], opacity: [0, 1, 0] }} transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.5, ease: "easeInOut" }} /> : null}
          <motion.div className="absolute -top-3 -right-3 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={t(0.2, 0.4)}><Sparkles className="h-3.5 w-3.5" /></motion.div>
        </div>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <motion.div key={f} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-sm" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={t(0.9 + i * 0.5, 0.4)}>
              <span className="text-muted-foreground w-16">{f}</span><span className="h-1.5 w-12 rounded bg-foreground/60" /><Check className="h-3.5 w-3.5 text-success" />
            </motion.div>
          ))}
        </div>
      </div>
    </Frame>
  );
};

/* 3. Statuser dukker opp én og én */
export const BadgesScene = () => {
  const { t } = useAnim();
  const pills = [["Til betaling", "status-til_betaling"], ["Forfaller snart", "status-forfaller_snart"], ["Forfalt", "status-forfalt"], ["Betalt", "status-betalt"]];
  return (
    <Frame>
      <div className="absolute inset-0 flex flex-col justify-center gap-2 px-8">
        {pills.map(([label, cls], i) => (
          <motion.div key={label} className="flex items-center justify-between rounded-md border bg-card px-3 py-1.5 shadow-sm" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={t(0.2 + i * 0.45, 0.4)}>
            <div className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-muted-foreground" /><span className="h-1.5 w-20 rounded bg-muted-foreground/30" /></div>
            <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-medium", cls)}>{label}</span>
          </motion.div>
        ))}
      </div>
    </Frame>
  );
};

/* 4. Knapp trykkes – stor hake */
export const CheckScene = () => {
  const { t, reduced } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-6 px-8">
        <div className="w-40 rounded-lg border bg-card shadow-sm p-3 space-y-2">
          <div className="h-1.5 w-2/3 rounded bg-muted-foreground/30" />
          <div className="h-1.5 w-1/2 rounded bg-muted-foreground/20" />
          <motion.div className="mt-2 h-7 rounded-md bg-primary text-primary-foreground text-[11px] font-medium flex items-center justify-center" animate={reduced ? {} : { scale: [1, 0.94, 1] }} transition={{ delay: 0.8, duration: 0.35 }}>Bekreft</motion.div>
        </div>
        <motion.div className="h-16 w-16 rounded-full status-betalt border flex items-center justify-center" initial={{ scale: 0, rotate: -30 }} animate={{ scale: [0, 1.15, 1], rotate: 0 }} transition={t(1.2, 0.5)}>
          <Check className="h-9 w-9" strokeWidth={3} />
        </motion.div>
      </div>
    </Frame>
  );
};

/* 5. Skjema fylles ut */
export const FormScene = () => {
  const { t } = useAnim();
  const rows = [["Navn", "w-24"], ["Org.nr", "w-16"], ["Adresse", "w-28"]];
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="w-64 rounded-lg border bg-card shadow-sm p-3 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-medium"><Building2 className="h-3.5 w-3.5 text-primary" /> Firmaprofil</div>
          {rows.map(([label, w], i) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground w-12">{label}</span>
              <div className="flex-1 h-6 rounded border bg-background px-1.5 flex items-center">
                <motion.span className={cn("h-1.5 rounded bg-foreground/60", w)} initial={{ scaleX: 0, originX: 0 }} animate={{ scaleX: 1 }} transition={t(0.3 + i * 0.5, 0.5)} />
              </div>
            </div>
          ))}
          <motion.div className="h-6 rounded-md bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center" initial={{ opacity: 0.5 }} animate={{ opacity: 1 }} transition={t(1.9, 0.3)}>Lagre</motion.div>
        </div>
      </div>
    </Frame>
  );
};

/* 6. Modulvelger som bytter */
export const SwitchScene = () => {
  const { reduced } = useAnim();
  const tabs = [["Regninger", FileText], ["Faktura", Receipt], ["Passord", Lock]];
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="relative flex gap-1 rounded-xl border bg-card p-1 shadow-sm">
          <motion.div className="absolute top-1 bottom-1 w-[92px] rounded-lg bg-accent" initial={{ left: 4 }} animate={reduced ? { left: 4 } : { left: [4, 100, 196, 4] }} transition={{ duration: 4.5, times: [0, 0.33, 0.66, 1], repeat: Infinity, repeatDelay: 0.8, ease: "easeInOut" }} />
          {tabs.map(([label, Icon]) => (
            <div key={label} className="relative z-10 w-[92px] flex flex-col items-center gap-1 py-2 text-[11px] font-medium"><Icon className="h-4 w-4 text-primary" />{label}</div>
          ))}
        </div>
      </div>
    </Frame>
  );
};

/* 7. Kalender + varsel */
export const CalendarScene = () => {
  const { t, reduced } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-6 px-8">
        <div className="w-40 rounded-lg border bg-card shadow-sm p-2">
          <div className="text-[10px] font-medium text-muted-foreground mb-1 px-1">Oktober</div>
          <div className="grid grid-cols-7 gap-1">
            {[...Array(28)].map((_, i) => (
              <motion.span key={i} className={cn("h-4 rounded text-[8px] flex items-center justify-center", i === 17 ? "bg-primary text-primary-foreground font-semibold" : "bg-secondary text-muted-foreground")} initial={i === 17 ? { scale: 1 } : false} animate={i === 17 && !reduced ? { scale: [1, 1.35, 1] } : {}} transition={{ delay: 0.6, duration: 0.5 }}>{i + 1}</motion.span>
            ))}
          </div>
        </div>
        <div className="relative">
          <motion.div className="h-14 w-14 rounded-full bg-accent flex items-center justify-center" animate={reduced ? {} : { rotate: [0, -12, 12, -8, 8, 0] }} transition={{ delay: 1.1, duration: 0.7, repeat: Infinity, repeatDelay: 2.5 }}><Bell className="h-7 w-7 text-primary" /></motion.div>
          <motion.div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border status-forfaller_snart px-2 py-0.5 text-[10px] font-medium" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={t(1.5, 0.4)}>Forfaller om 3 dager</motion.div>
        </div>
      </div>
    </Frame>
  );
};

/* 8. Nøkkel + lås (hvelv) */
export const KeyScene = () => {
  const { t, reduced } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-8 px-8">
        <motion.div initial={{ x: -40, rotate: -40, opacity: 0 }} animate={{ x: 0, rotate: 0, opacity: 1 }} transition={t(0.2, 0.7)} className="text-primary"><KeyRound className="h-10 w-10" /></motion.div>
        <div className="relative h-20 w-20 rounded-2xl border-2 border-primary/40 bg-accent/70 flex items-center justify-center">
          <motion.div className="absolute" initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={t(1.1, 0.2)}><Lock className="h-9 w-9 text-primary" /></motion.div>
          <motion.div className="absolute" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={t(1.2, 0.3)}><Unlock className="h-9 w-9 text-primary" /></motion.div>
          {!reduced ? <motion.div className="absolute inset-0 rounded-2xl ring-4 ring-primary/40" initial={{ opacity: 0, scale: 1 }} animate={{ opacity: [0, 0.8, 0], scale: [1, 1.25, 1.35] }} transition={{ delay: 1.2, duration: 0.9 }} /> : null}
        </div>
        <motion.div className="rounded-md border bg-card shadow-sm px-2.5 py-1.5 text-[10px] font-mono tracking-wider" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={t(1.6, 0.4)}>K7Q2-9XMA-4T…</motion.div>
      </div>
    </Frame>
  );
};

/* 9. To personer kobles (fullmakt/deling) */
export const PeopleScene = () => {
  const { t } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="relative flex items-center gap-16">
          {[0, 1].map((i) => (
            <motion.div key={i} className="h-14 w-14 rounded-full bg-accent border-2 border-primary/30 flex items-center justify-center" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={t(0.2 + i * 0.4, 0.4)}><User className="h-7 w-7 text-primary" /></motion.div>
          ))}
          <svg className="absolute left-14 right-14 top-1/2 -translate-y-1/2 h-2 w-16" viewBox="0 0 64 8" aria-hidden="true">
            <motion.line x1="0" y1="4" x2="64" y2="4" stroke="currentColor" className="text-primary" strokeWidth="2" strokeDasharray="4 4" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={t(0.9, 0.7)} />
          </svg>
          <motion.span className="absolute left-1/2 -translate-x-1/2 -bottom-8 whitespace-nowrap rounded-full border status-ma_kontrolleres px-2 py-0.5 text-[10px] font-medium" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={t(1.5, 0.4)}>Innsyn gitt</motion.span>
        </div>
      </div>
    </Frame>
  );
};

/* 10. Bilag kobles til faktura */
export const LinkScene = () => {
  const { t } = useAnim();
  return (
    <Frame>
      <div className="absolute inset-0 flex items-center justify-center gap-4 px-8">
        <motion.div className="w-24 rounded-lg border bg-card shadow-sm p-2 text-[10px]" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={t(0.2, 0.4)}>
          <div className="flex items-center gap-1 font-medium mb-1"><Archive className="h-3 w-3 text-primary" /> Bilag</div>
          <div className="h-1.5 w-3/4 rounded bg-muted-foreground/30 mb-1" /><div className="h-1.5 w-1/2 rounded bg-muted-foreground/20" />
        </motion.div>
        <motion.div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" initial={{ scale: 0 }} animate={{ scale: [0, 1.2, 1] }} transition={t(1.0, 0.4)}><Link2 className="h-4 w-4" /></motion.div>
        <motion.div className="w-24 rounded-lg border bg-card shadow-sm p-2 text-[10px]" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={t(0.5, 0.4)}>
          <div className="flex items-center gap-1 font-medium mb-1"><Receipt className="h-3 w-3 text-primary" /> Faktura 1042</div>
          <div className="h-1.5 w-2/3 rounded bg-muted-foreground/30 mb-1" /><div className="h-1.5 w-1/2 rounded bg-muted-foreground/20" />
        </motion.div>
      </div>
    </Frame>
  );
};

const SCENES = { flow: FlowScene, scan: ScanScene, badges: BadgesScene, check: CheckScene, form: FormScene, switch: SwitchScene, calendar: CalendarScene, key: KeyScene, people: PeopleScene, link: LinkScene };

export const Scene = ({ name, ...props }) => {
  const Cmp = SCENES[name] || ScanScene;
  return <Cmp {...props} />;
};

